import React, { useEffect, useRef, useState } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import BottomSheet from './BottomSheet';
import SettingsSheetTitle from './ui/SettingsSheetTitle';
import Button from './ui/FitButton';
import WebviewMessenger from '../utils/WebviewMessenger';
import { localize } from '../utils/interfaceCopy';

const buttonStyle = {
    minHeight: 52, padding: '14px', my: '6px', borderRadius: '12px', boxShadow: 'none',
    bgcolor: '#ffe566', color: '#5a4a00', fontSize: '1rem', lineHeight: 1.5, fontWeight: 700,
    textTransform: 'none', letterSpacing: 0, '&:hover': { bgcolor: '#ffe566', boxShadow: 'none' }, '&:active': { opacity: .75 },
    '&.Mui-disabled': { bgcolor: '#ffe566', color: '#5a4a00', opacity: .45 },
};
const secondaryStyle = { ...buttonStyle, bgcolor: '#f2f2f2', color: '#1f1f1f', border: 0,
    '&:hover': { bgcolor: '#e8eaed', border: 0, boxShadow: 'none' }, '&.Mui-disabled': { bgcolor: '#f2f2f2', color: '#1f1f1f', border: 0, opacity: .45 } };

function LockMark() {
    return <Box aria-hidden="true" sx={{ width: 56, height: 56, borderRadius: '20px', bgcolor: '#fff9e6', position: 'relative', mb: '20px' }}>
        <Box sx={{ position: 'absolute', width: 16, height: 15, border: '2px solid #8a7000', borderRadius: '9px 9px 0 0', top: 13, left: 20 }} />
        <Box sx={{ position: 'absolute', width: 24, height: 19, border: '2px solid #8a7000', borderRadius: '5px', top: 24, left: 16,
            bgcolor: '#fff9e6', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Box sx={{ width: 3, height: 6, bgcolor: '#8a7000', borderRadius: '2px' }} />
        </Box>
    </Box>;
}

function SettingRow({ title, description, label = title, checked, disabled, loading, onClick }) {
    const toggle = typeof checked === 'boolean';
    return <Box component="button" type="button" role={toggle ? 'switch' : undefined} aria-checked={toggle ? checked : undefined}
        aria-label={label} aria-busy={loading || undefined} disabled={disabled} onClick={onClick}
        sx={{ display: 'flex', alignItems: 'center', gap: '12px', width: '100%', minHeight: 64, py: '12px', px: 0,
            border: 0, borderRadius: '12px', bgcolor: 'transparent', color: '#1a1a1a', textAlign: 'left', font: 'inherit', cursor: 'pointer',
            WebkitTapHighlightColor: 'transparent',
            '&:active': { bgcolor: toggle ? 'transparent' : '#f5f5f5' }, '&:disabled': { cursor: 'default' } }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontSize: '1.05rem', fontWeight: 700, lineHeight: 1.5, letterSpacing: '.16px' }}>{title}</Typography>
            <Typography sx={{ fontSize: '0.96rem', color: '#777', mt: '1.6px', lineHeight: 1.5, letterSpacing: '.144px', overflowWrap: 'anywhere' }}>{description}</Typography>
        </Box>
        {toggle ? <Box component="span" aria-hidden="true" data-privacy-switch-track sx={{ display: 'flex', alignItems: 'center',
            justifyContent: checked ? 'flex-end' : 'flex-start', width: 46, height: 28, boxSizing: 'border-box', p: '3px',
            borderRadius: '14px', bgcolor: checked ? '#ffe566' : '#e5e7eb', flexShrink: 0 }}>
            <Box component="span" data-privacy-switch-thumb sx={{ width: 22, height: 22, borderRadius: '11px', bgcolor: '#fff',
                display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {loading && <CircularProgress size={16} sx={{ color: '#8a7000' }} />}
            </Box>
        </Box> : <Box component="span" aria-hidden="true" sx={{ width: 20, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <Box component="span" data-privacy-chevron sx={{ width: 8, height: 8, borderRight: '1.5px solid #aaa', borderTop: '1.5px solid #aaa', transform: 'rotate(45deg)', mr: '6px' }} />
        </Box>}
    </Box>;
}

// Only presentation lives here. Commands carry no PIN or caller-supplied state;
// native PrivacyScreen/Store still authorize each action and confirm persistence.
export default function AppPrivacySettingsSheet({ open, onClose }) {
    const [view, setView] = useState(null);
    const [waiting, setWaiting] = useState(false);
    const [error, setError] = useState('');
    const session = useRef(null);
    const timeout = useRef(null);
    const closeRef = useRef(onClose); closeRef.current = onClose;
    useEffect(() => { WebviewMessenger.send({ type: 'REQUEST_APP_RUNTIME_INFO' }); }, []);
    const startTimeout = () => {
        clearTimeout(timeout.current);
        timeout.current = setTimeout(() => { setWaiting(false); setError(localize('처리하지 못했어요. 잠시 후 다시 시도해주세요.')); }, 8000);
    };
    useEffect(() => {
        if (!open) return undefined;
        const id = `privacy_${Date.now()}_${Math.random().toString(36).slice(2)}`;
        session.current = id; setView(null); setError(''); setWaiting(true);
        const unsubscribe = WebviewMessenger.onMessage(message => {
            if (message?.type !== 'PRIVACY_SETTINGS_VIEW' || message.session !== id || session.current !== id) return;
            clearTimeout(timeout.current); setWaiting(false); setError('');
            if (message.closed) { closeRef.current(); return; }
            if (message.view) setView(message.view);
        });
        startTimeout();
        WebviewMessenger.send({ type: 'OPEN_PRIVACY_SETTINGS', data: { session: id } });
        return () => {
            session.current = null; clearTimeout(timeout.current); unsubscribe();
            WebviewMessenger.send({ type: 'PRIVACY_SETTINGS_ACTION', data: { session: id, action: 'close' } });
        };
    }, [open]);
    const action = name => {
        if (!session.current || waiting || view?.busy) return;
        setWaiting(true); setError(''); startTimeout();
        WebviewMessenger.send({ type: 'PRIVACY_SETTINGS_ACTION', data: { session: session.current, action: name } });
    };
    const busy = waiting || !!view?.busy;
    const offer = view?.phase === 'biometricOffer';
    const close = () => { if (!view?.busy && !view?.nativeVisible) {
        if (offer) action('skipBiometric'); else closeRef.current();
    } };
    // Mount the final-height menu before the common entrance animation. Opening
    // a short spinner first and expanding rows mid-slide causes a visible jump.
    // A native PIN screen temporarily covers this sheet. Keep its presentation
    // mounted so returning does not replay the backdrop fade/entrance animation.
    return <Box data-privacy-settings-surface aria-hidden={!!view?.nativeVisible} inert={view?.nativeVisible ? '' : undefined}
        sx={{ visibility: view?.nativeVisible ? 'hidden' : 'visible' }}>
        <BottomSheet open={open && (view?.ready || (!view && !!error))} onClose={close} dismissDisabled={!!view?.busy || !!view?.nativeVisible}
        ariaLabelledBy="app-privacy-settings-title" handleColor="#d1d1d1" maxHeight="90vh" flat>
        {!offer && <SettingsSheetTitle id="app-privacy-settings-title" sx={{ mb: 2 }}>{localize('앱 잠금을 설정해요')}</SettingsSheetTitle>}
        {!view?.ready ? <Box sx={{ py: 2, textAlign: 'center' }}>
            <Typography sx={{ color: '#777', mb: 2 }}>{localize('잠금 상태를 확인하고 있어요')}</Typography>
            {waiting && <CircularProgress size={24} />}
        </Box> : offer ? <>
            <Box sx={{ display: 'flex', alignItems: 'center', minHeight: 40, pb: '8px' }}>
                <Box component="button" aria-label={localize('뒤로')} onClick={() => action('skipBiometric')} disabled={busy}
                    sx={{ position: 'relative', width: 48, height: 48, ml: '-12px', mr: '4px', bgcolor: 'transparent', border: 0, borderRadius: '24px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Box sx={{ position: 'absolute', width: 11, height: 11, borderLeft: '2px solid #1a1a1a', borderBottom: '2px solid #1a1a1a', transform: 'rotate(45deg)' }} />
                    <Box sx={{ width: 19, height: 2, bgcolor: '#1a1a1a', ml: '6px' }} />
                </Box>
                <Typography sx={{ fontSize: '16px', lineHeight: '24px', fontWeight: 600 }}>{localize('앱 잠금')}</Typography>
            </Box>
            <Box sx={{ pt: '16px', mb: '16px' }}>
                <LockMark />
                <Typography id="app-privacy-settings-title" component="h2" sx={{ fontSize: '1.16rem', lineHeight: 1.5, fontWeight: 800, letterSpacing: '.15px', color: '#1a1a1a' }}>{localize('생체 인증으로 더 편하게 열까요?')}</Typography>
            </Box>
            <Typography sx={{ color: '#777', fontSize: '14.5px', lineHeight: '22px', mb: 2 }}>{localize('기기에 등록된 생체 정보로 간편하게 열 수 있어요.')}</Typography>
            <Button fullWidth variant="contained" disabled={busy} onClick={() => action('enableBiometric')} sx={buttonStyle}>{localize('생체 인증 사용하기')}</Button>
            <Button fullWidth variant="contained" disabled={busy} onClick={() => action('skipBiometric')} sx={secondaryStyle}>{localize('나중에')}</Button>
            <Typography sx={{ color: '#6b7280', fontSize: '13px', lineHeight: '20px', mt: 2 }}>{localize('설정에서 언제든 켜거나 끌 수 있어요.')}</Typography>
        </> : <>
            <SettingRow title={localize('잠금 사용')} description={localize(view.biometric ? '얼굴·지문 또는 비밀번호로 열어요' : '앱을 열 때 비밀번호를 입력해요')}
                label={localize(view.enabled ? '앱 잠금 끄기' : '앱 잠금 켜기')} checked={view.enabled} disabled={busy} onClick={() => action('lock')} />
            {view.enabled && (view.supportsBiometric || view.biometric) && <SettingRow title={localize('생체 인증')}
                description={localize('얼굴·지문으로 간편하게 열어요')} label={localize(view.biometric ? '생체 인증 끄기' : '생체 인증으로 해제')}
                checked={view.biometric} disabled={busy} onClick={() => action('biometric')} />}
            {view.enabled && <Box sx={{ mt: '12px', borderTop: '1px solid #eee', pt: '12px' }}>
                <SettingRow title={localize('비밀번호 변경')} description={localize('새 비밀번호를 설정해요')} disabled={busy} onClick={() => action('change')} />
            </Box>}
            {!view.recoveryAvailable && <Typography sx={{ color: '#777' }}>{localize('비밀번호를 복구하려면 앱을 업데이트해주세요')}</Typography>}
            <Box sx={{ mt: '12px', borderTop: '1px solid #eee', pt: '12px' }}>
                <SettingRow title={localize('약 이름 숨기기')} description={localize('알림·위젯에서 약 이름을 가려요')}
                    label={localize(view.hideNames ? '약 이름 숨김 끄기' : '약 이름 숨김 켜기')} checked={view.hideNames}
                    disabled={busy || view.hideNamesPending} loading={view.busy && view.busyFeedback === 'names'} onClick={() => action('names')} />
            </Box>
            {view.hideNamesPending && <Box sx={{ mt: '8px', p: '12px', bgcolor: '#f9fafb', borderRadius: '12px' }}>
                <Typography role="alert" sx={{ color: '#777' }}>{localize('표시 설정을 다시 확인해주세요')}</Typography>
                <Button fullWidth variant="contained" disabled={busy} onClick={() => action('retryNames')} sx={secondaryStyle}>{localize('설정 다시 적용')}</Button>
            </Box>}
        </>}
        {(error || view?.error) && <Typography role="alert" sx={{ color: '#b42318', mt: 2 }}>{error || view.error}</Typography>}
        </BottomSheet>
    </Box>;
}
